import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  FiservCardInterface,
  FiservCardLockedReasonsEnum,
  FiservCardStatusEnum,
  Hint,
  Inscription,
  LockIcon,
  Preloader,
  UnlockIcon,
  useResponsive,
} from '@common-fe/common-fe';

import { useIsLockCardPurseAvailable } from '@/modules/transaction/components/Cards/hooks/useIsLockCardPurseAvailable';

import { CardStatusActionWrap } from '../ChangeStatusCardModal.styles';

import CardActionCardHeader from './CardActionCardHeader';

const lockReasonTitles: Record<FiservCardLockedReasonsEnum, string> = {
  [FiservCardLockedReasonsEnum.REPAYMENT_REQUIRED]: 'Repayment required',
  [FiservCardLockedReasonsEnum.DOCUMENTATION_REQUIRED]: 'Documentation required',
  [FiservCardLockedReasonsEnum.FRAUD_DETECTED]: 'Fraud detected',
  [FiservCardLockedReasonsEnum.CARDHOLDERS_REQUEST]: 'Cardholders request',
};

const LOCKED_STATUSES = [FiservCardStatusEnum.FROZEN, FiservCardStatusEnum.PENDING_FROZEN];

interface CardCloseActionCardProps {
  card: FiservCardInterface;
  loadingButton: boolean;
  onOpenLockModal: () => void;
  onOpenLockUnlockCardModal: () => void;
}

const CardLockUnlockActionCard: React.FC<CardCloseActionCardProps> = ({
  card,
  loadingButton,
  onOpenLockModal,
  onOpenLockUnlockCardModal,
}) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const isLockCardPurseAvailable = useIsLockCardPurseAvailable();
  const isUnlockDisabled = useMemo(() => card?.employeeUnlockForbidden, [card]);
  const isCardLocked = useMemo(() => LOCKED_STATUSES.includes(card.status), [card.status]);

  const buttonLabel = useMemo(() => {
    if (!isLockCardPurseAvailable) {
      return t('Update Card Access');
    }
    return isCardLocked ? t('Unlock Card') : t('Lock Card');
  }, [isLockCardPurseAvailable, isCardLocked, t]);

  const title = isLockCardPurseAvailable ? buttonLabel : t('Lock/Unlock Card');
  const description = isLockCardPurseAvailable
    ? (isMobile ? (
      t('You can unlock this card at any time.')
    ) : (
      <>
        {t('You can unlock this card')}
        <br />
        {t('at any time.')}
      </>
    ))
    : (isMobile ? (
      t('Manage card and account access.')
    ) : (
      <>
        {t('Manage card and')}
        <br />
        {t('account access.')}
      </>
    ));

  return (
    <Box width={isMobile ? '100%' : undefined}>
      <CardStatusActionWrap
        background="canvas"
        round="container1Round"
        $isMobile={isMobile}
        border={{ color: 'border2', size: 'small' }}
      >
        <CardActionCardHeader
          icon={isCardLocked && isLockCardPurseAvailable ? (
            <UnlockIcon size="xlarge" color="iconAccent" />
          ) : (
            <LockIcon size="xlarge" color="iconAccent" />
          )}
          title={title}
          description={description}
          descriptionLineHeight="20px"
        />
        <Hint
          hintAlign="vertical"
          hintElement={
            <AppButton
              buttonType="primary"
              type="submit"
              width="100%"
              testId="card-lock-unlock-action-card_button"
              style={{ height: '40px', paddingInline: '12px' }}
              disabled={loadingButton || isUnlockDisabled}
              onClick={isLockCardPurseAvailable ? onOpenLockUnlockCardModal : onOpenLockModal}
            >
              {loadingButton ? (
                <Box width="100%" align="center">
                  <Preloader color="white" />
                </Box>
              ) : (
                buttonLabel
              )}
            </AppButton>
          }
          hideHint={!isUnlockDisabled}
        >
          <Inscription size="small" weight={500} lineHeight="18px" textAlign="center">
            {t(
              `${card.lockReason ? `${lockReasonTitles[card.lockReason]}.` : ''} Card access cannot be modified at this time`
            )}
          </Inscription>
        </Hint>
      </CardStatusActionWrap>
    </Box>
  );
};

export default CardLockUnlockActionCard;
